import React, { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18n-lite'

import {
  Button,
  Icon,
  NavigationSlider,
  useIsMobile
} from '@yes.technology/react-toolkit'

import { useSiteState } from 'siteState/shared'

import { FilterFieldsDisplayOptions } from '../../FilterFields'

import {
  CenterWrapper,
  FirstElement,
  GroupSideButtons,
  StyledCollapsibleCard,
  StyledFilterButton
} from './GroupControls.styles'

export type GroupControlsProp = {
  elementUuid: string
  sectionPosition: number
  sectionsLength: number
  setSectionPosition: (section: number) => void
  groupPosition: number
  groupsLength: number
  setGroupPosition: (group: number) => void
  handleClearCurrentGroup: () => void
  onRemoveCurrentGroup: () => void
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  enableComposition?: boolean
  children: React.ReactNode
  shouldHideFilters: boolean
}

export default function GroupControls({
  sectionsLength,
  sectionPosition,
  setSectionPosition,
  groupPosition,
  groupsLength,
  handleClearCurrentGroup,
  onRemoveCurrentGroup,
  setGroupPosition,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  children,
  shouldHideFilters,
  elementUuid,
  enableComposition
}: GroupControlsProp) {
  const { t } = useTranslation()

  const collapsibleCardRef = useRef({
    onOpen: () => ({}),
    onClose: () => ({}),
    onToggle: () => ({})
  })

  const isMobile = useIsMobile()

  const areaLineTitle = (
    <FirstElement color={navigationSliderColor}>
      {t('report.filter-area.filter-head.filter-group')}
    </FirstElement>
  )

  const leftHeaderElement = isMobile ? undefined : areaLineTitle

  const commonControls = (
    <>
      <NavigationSlider
        heightClass='small'
        hierarchyLevel='level_3'
        currentPosition={groupPosition}
        first={1}
        total={groupsLength}
        afterClick={setGroupPosition}
        textForCurrent='Grupo'
        arrowOrientationButtons='diagonalArrows'
        backgroundColor={navigationSliderBackgroundColor}
        color={navigationSliderColor}
      />
      <GroupSideButtons>
        <StyledFilterButton
          heightClass='regular'
          onClick={handleClearCurrentGroup}
          variant='secondary'
          aria-label={t('filter-fields.clean')}
        >
          {t('filter-fields.clean')}
        </StyledFilterButton>
        {groupsLength > 1 && (
          <StyledFilterButton
            heightClass='regular'
            variant='secondary'
            onClick={onRemoveCurrentGroup}
            aria-label={t('filter-fields.remove-group')}
          >
            <Icon iconName='Cleaner' icSize='regular' />
          </StyledFilterButton>
        )}
      </GroupSideButtons>
    </>
  )

  const centerHeaderElement = (
    <CenterWrapper>
      <NavigationSlider
        ignoreHeight
        heightClass='small'
        currentPosition={sectionPosition}
        first={1}
        total={sectionsLength}
        afterClick={setSectionPosition}
        textForCurrent={t('filter-fields.section')}
        hierarchyLevel='level_3'
        backgroundColor={navigationSliderBackgroundColor}
        color={navigationSliderColor}
      />
      {isMobile && commonControls}
    </CenterWrapper>
  )

  const rightHeaderElement =
    isMobile || shouldHideFilters || !enableComposition
      ? undefined
      : commonControls

  const [displayOptions] = useSiteState<FilterFieldsDisplayOptions>(
    'filter-display-options'
  )

  const displayOptionsElement = displayOptions?.[elementUuid]

  useEffect(() => {
    if (!shouldHideFilters) {
      collapsibleCardRef?.current?.onOpen()
      return
    }

    collapsibleCardRef?.current?.onClose()
  }, [shouldHideFilters])

  if (displayOptionsElement?.hideFieldsCollapsibleContainer)
    return <>{children}</>

  return (
    <StyledCollapsibleCard
      data-testid='group-controls'
      leftHeaderElement={leftHeaderElement}
      centerHeaderElement={centerHeaderElement}
      rightHeaderElement={rightHeaderElement}
      heightClass='small'
      headerColor='blue'
      alignSecondPosition='center'
      ref={collapsibleCardRef}
      defaultOpenState
      wrapRightAreaElement
      backgroundColor={navigationSliderBackgroundColor}
      color={navigationSliderColor}
      areaLineProps={{
        colorScheme: 'gray',
        className: isMobile ? 'p-2' : ''
      }}
      {...(!shouldHideFilters && {
        defaultOpenState: true
      })}
    >
      {children}
    </StyledCollapsibleCard>
  )
}
